import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import {
  Plus,
  Sparkles,
  AlertTriangle,
  Loader2,
  Info,
  ListChecks,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  useGetMyQuizzesQuery,
  useDeleteQuizMutation,
} from '../../api/apiSlice';
import QuizCard from '../../components/quiz/QuizCard';
import ShareModal from '../../components/quiz/ShareModal';

const MyQuizzesPage = () => {
  const { t } = useTranslation();
  const { data, isLoading, error, refetch } = useGetMyQuizzesQuery();
  const [deleteQuiz] = useDeleteQuizMutation();
  const [shareQuiz, setShareQuiz] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const items = data?.items || [];
  const limit = data?.limit ?? 2;
  const activeCount = data?.activeCount ?? 0;
  const canCreate = data?.canCreate ?? false;

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteQuiz(confirmDelete._id).unwrap();
      toast.success(t('quiz.myList.deleted'));
      setConfirmDelete(null);
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || t('quiz.myList.deleteError'));
    }
  };

  return (
    <div className="min-h-[60vh]">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between mb-6">
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
              {t('quiz.myList.title')}
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {t('quiz.myList.activeCounter', { count: activeCount, limit })}
            </p>
          </div>

          <Link
            to="/dashboard/student/quizzes/create"
            onClick={(e) => {
              if (!canCreate) {
                e.preventDefault();
                toast.error(t('quiz.myList.limitToast', { limit }));
              }
            }}
            className={`inline-flex items-center gap-2 rounded-2xl px-5 py-3 text-sm font-semibold shadow-md transition ${
              canCreate
                ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-500 cursor-not-allowed'
            }`}
          >
            <Plus className="w-4 h-4" /> {t('quiz.myList.createButton')}
          </Link>
        </div>

        {/* Info banner */}
        <div className="mb-6 rounded-2xl border border-blue-200 dark:border-blue-900/40 bg-blue-50 dark:bg-blue-950/20 p-4 flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800 dark:text-blue-200">
            <Trans
              i18nKey="quiz.myList.infoBanner"
              values={{ limit }}
              components={{ b: <b /> }}
            />
          </div>
        </div>

        {/* Limit warning */}
        {!canCreate && (
          <div className="mb-6 rounded-2xl border border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-950/20 p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800 dark:text-amber-200">
              {t('quiz.myList.limitWarning')}
            </div>
          </div>
        )}

        {/* Body */}
        {isLoading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        ) : error ? (
          <div className="rounded-2xl border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-950/20 p-4 text-red-700 dark:text-red-300">
            {error?.data?.message || t('quiz.myList.loadError')}
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-gray-300 dark:border-gray-700 p-10 text-center">
            <ListChecks className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
              {t('quiz.myList.empty')}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-5 max-w-md mx-auto">
              {t('quiz.myList.emptyHint')}
            </p>
            <Link
              to="/dashboard/student/quizzes/create"
              className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-3 text-sm font-semibold"
            >
              <Plus className="w-4 h-4" /> {t('quiz.myList.createFirst')}
            </Link>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {items.map((q) => (
              <QuizCard
                key={q._id}
                quiz={q}
                onShare={(quiz) => setShareQuiz(quiz)}
                onDelete={(quiz) => setConfirmDelete(quiz)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Share modal */}
      {shareQuiz && (
        <ShareModal
          quizId={shareQuiz._id}
          quizTitle={shareQuiz.title}
          onClose={() => setShareQuiz(null)}
        />
      )}

      {/* Delete confirm */}
      {confirmDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => setConfirmDelete(null)}
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-2xl p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="rounded-full bg-red-100 dark:bg-red-500/10 p-2.5">
                <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('quiz.myList.confirmDeleteTitle')}
              </h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-5 break-words">
              {t('quiz.myList.confirmDeleteBody', { title: confirmDelete.title })}
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmDelete(null)}
                className="rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                {t('quiz.myList.cancel')}
              </button>
              <button
                onClick={handleDelete}
                className="rounded-xl bg-red-600 hover:bg-red-700 px-4 py-2 text-sm font-semibold text-white"
              >
                {t('quiz.myList.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyQuizzesPage;
